import { faInfo, faPencilAlt, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addDays,
  format,
  isAfter,
  isBefore,
  isWithinInterval,
  startOfDay,
  subDays,
} from "date-fns";
import { cloneDeep } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FieldProps } from "react-jsonschema-form";
import { usePropertyConfiguration } from "../../../../components/property-configuration-provider";
import { useProperty } from "../../../../components/property-provider";
import { useApiClient } from "../../../../lib/http";
import { logSafeError } from "../../../../lib/safeLog";
import {
  Amount,
  InsuranceSelected,
  RoomStayTaxation,
  SelectedService,
} from "../../../modification/model";
import { ExtraType } from "../../../extras/types";
import { RATE_TYPE, DATE_SELECTION_TYPE, EXTRA_VARIANT } from "../../../witadmin/Common/constants";
import DatePickerGroup from "../../../witadmin/CRUD/Common/DatePickerGroup";
import FieldErrorList from "../../../witadmin/CRUD/Common/FieldErrorList";
import InputGroup from "../../../witadmin/CRUD/Common/InputGroup";
import TextAreaGroup from "../../../witadmin/CRUD/Common/TextAreaGroup";
import GridRow from "./gridRow";
import { messages } from "./messages";
import {
  BookingDetails,
  DatePickerGroupWrapper,
  ExtrasTotal,
  GrandTotalWrapper,
  Grid,
  GridAppliedPrice,
  GridAriPrice,
  GridDate,
  GridDiscount,
  GridFinalPrice,
  GridPromos,
  GridRestrictions,
  GridSeparator,
  HelpTextMessage,
  HelpTextMessageNoMargin,
  NetPriceTotalWrapper,
  PriceLine,
  StyledFontAwesomeIconWrapper,
  StyledModificationPriceManagement,
  StyledReactTooltip,
} from "./styles";
import { AriValues, FinalPrice, Row } from "./types";
import { generateRows } from "./utils";
import swal from "sweetalert";
import { CancellationPolicies } from "./CancellationPolicies";
import ExtraSelection from "./ExtraSelection";
import { Extra, ExtraSelectionEntity, ExtraWithFlags } from "./domain/extraSelection.entity";
import { CustomModal } from "../../../../components/modal";
import { ExtraPriceModification } from "./ExtraPriceModification";
import { usePropertyData } from "../../../../components/property-admin-data-provider";
import { useExtrasQuery, usePropertyAriQuery } from "./extraQueries";
import { useLocaleProvider } from "../../../../components/locale-provider";
import AccommodationSelector from "./components/AccommodationSelector";
import {
  buildGridSyncKey,
  getInvalidDailyValueDates,
  getStayNightTickers,
  getUnratedStayDates,
} from "../../../modification/dailyValuesDates";

const regex = RegExp(/^[0-9]\d{0,99}(\.\d{0,2})?%?$/);

const parseVariantDateSelectionType = (variant: string): keyof typeof DATE_SELECTION_TYPE => {
  if (variant && variant.includes(EXTRA_VARIANT.SPECIFIC_DATE)) {
    return DATE_SELECTION_TYPE.SPECIFIC as keyof typeof DATE_SELECTION_TYPE;
  }
  if (variant && variant.includes(EXTRA_VARIANT.PER_DAY)) {
    return DATE_SELECTION_TYPE.ALL as keyof typeof DATE_SELECTION_TYPE;
  }
  return DATE_SELECTION_TYPE.NONE as keyof typeof DATE_SELECTION_TYPE;
};

const isVariantObligatory = (variant: string): boolean => {
  return Boolean(variant && variant.endsWith(EXTRA_VARIANT.MANDATORY));
};

const TAXATION_TICKER_PREFIX = "service_tax_";

const toNameKey = (name: string): string => name.toLowerCase().replace(/\s+/g, "_");

// Resolves the catalog extra a taxation (fromExtra) was built from. New format encodes
// the extra ticker directly (`service_tax_<extraTicker>`); older reservations encoded the
// extra name (`service_tax_` + name.toLowerCase() with spaces replaced by `_`), matched
// here against each catalog extra's transformed name.
const findCatalogExtraForTaxation = <T extends { ticker: string; name: string }>(
  taxationTicker: string,
  catalog: T[]
): T | undefined => {
  const suffix = taxationTicker.replace(TAXATION_TICKER_PREFIX, "");
  return (
    catalog.find(extra => extra.ticker === suffix) ||
    catalog.find(extra => toNameKey(extra.name) === suffix)
  );
};

const resolveTaxationExtraTicker = <T extends { ticker: string; name: string }>(
  taxationTicker: string,
  catalog: T[]
): string =>
  findCatalogExtraForTaxation(taxationTicker, catalog)?.ticker ??
  taxationTicker.replace(TAXATION_TICKER_PREFIX, "");

export function ModificationPriceManagement({
  formData,
  onChange,
  rawErrors,
  disabled,
  schema,
  isRestrictedModification,
}: FieldProps & { isRestrictedModification?: boolean }) {
  const intl = useIntl();
  const propertyData = usePropertyData();
  const locale = useLocaleProvider();

  const selectorDisabled = disabled && !isRestrictedModification;
  const datesDisabled = disabled && !isRestrictedModification;
  const showPriceGrid = !disabled || !!isRestrictedModification;

  const { ticker: propertyTicker, info } = useProperty();
  const propertyConfiguration = usePropertyConfiguration();
  const newAriEnabled = !!Number(propertyConfiguration?.showNewAriManagement);
  const notAllowCustomerCommentEdit = Number(propertyConfiguration?.allowCustomerCommentEdit) === 0;
  const apiClient = useApiClient();

  const [rows, setRows] = useState<Row[]>([]);
  const [initialAriFetched, setInitialAriFetched] = useState<boolean>(false);
  const [finalPrices, setFinalPrices] = useState<FinalPrice[]>([]);
  const [originalExtras] = useState(() => formData.selectedServices);
  const [originalName] = useState(() => formData.fullName);
  const [referenceExtras, setReferenceExtras] = useState<SelectedService[]>([]);
  const [isCancellationPoliciesModalOpen, setIsCancellationPoliciesModalOpen] = useState<boolean>(
    false
  );
  const [isExtraSelectionModalOpen, setIsExtraSelectionModalOpen] = useState<boolean>(false);
  const [isPriceModificationModalOpen, setIsPriceModificationModalOpen] = useState<boolean>(false);
  const [
    selectedServiceForPriceEdit,
    setSelectedServiceForPriceEdit,
  ] = useState<SelectedService | null>(null);
  const [scrollToExtraTicker, setScrollToExtraTicker] = useState<string | null>(null);
  const prevCheckIn = useRef<string>("");
  const prevCheckOut = useRef<string>("");
  const prevSelectedInventoryTicker = useRef<string>("");
  // Sequence token for fetchData: only the most recent invocation may update the grid.
  const fetchSeq = useRef<number>(0);
  const [deletedTaxationTickers, setDeletedTaxationTickers] = useState<string[]>([]);
  const [selectedInventoryTicker, setSelectedInventoryTicker] = useState<string>(
    () => formData?.inventoryTicker || ""
  );

  const currentInventory = useMemo(() => {
    if (!propertyData?.inventories || !selectedInventoryTicker) {
      return undefined;
    }
    return propertyData.inventories.find(inv => inv.ticker === selectedInventoryTicker);
  }, [propertyData?.inventories, selectedInventoryTicker]);

  const hasVirtualInventories = useMemo(() => {
    return propertyData?.inventories?.some(it => it.virtualInventory?.length > 0);
  }, [propertyData?.inventories]);

  const { data: allExtrasList } = useExtrasQuery(propertyTicker);

  const { data: propertyAri } = usePropertyAriQuery(
    propertyTicker,
    formData.checkIn,
    formData.checkOut,
    selectedInventoryTicker,
    currentInventory
  );

  const transformedExtrasList = useMemo(() => {
    if (!allExtrasList) return [];
    return allExtrasList
      .filter(e => e.active)
      .map(extra => {
        const currentLocale = intl.locale || "en";
        const translation =
          extra.translations.find(t => t.locale === currentLocale) || extra.translations[0];
        const name = translation?.name || extra.ticker;

        const variant = extra.variant;
        const type = extra.type;
        const obligatory = isVariantObligatory(variant);
        const dateSelectionType = parseVariantDateSelectionType(variant);

        const rateAmount = extra.rate.value || 0;
        const percentage = extra.rate.type === RATE_TYPE.PERCENTAGE;

        const defaultDate = formData?.checkIn;
        const dateQuantity = {
          [defaultDate]: 0,
        };

        const quantity = 0;

        return {
          ticker: extra.ticker,
          name: name,
          quantity: quantity,
          amount: {
            amount: rateAmount,
            currency: info?.defaultCurrency || "EUR",
          },
          obligatory: obligatory,
          percentage: percentage,
          dateQuantity: dateQuantity,
          type: type,
          dateSelectionType: dateSelectionType,
          appliesToGuests: extra.appliesToGuests || [],
          isAppliedToCurrentInventoryTicker: Boolean(
            extra.appliesTo[0].tickers.includes(formData.inventoryTicker)
          ),
          isAvailable: Boolean(
            propertyAri &&
              propertyAri[propertyTicker].arivalues &&
              propertyAri[propertyTicker].arivalues[extra.ticker]
          ),
          isRestricted: Boolean(
            propertyAri &&
              propertyAri[propertyTicker].arivalues &&
              propertyAri[propertyTicker].arivalues[extra.ticker] &&
              propertyAri[propertyTicker].arivalues[extra.ticker].restricted
          ),
        };
      });
  }, [
    allExtrasList,
    intl.locale,
    info?.defaultCurrency,
    formData?.checkIn,
    formData.inventoryTicker,
    propertyAri,
    propertyTicker,
  ]);

  const extras = useMemo<SelectedService[]>(() => {
    const selectedServices: SelectedService[] = formData?.selectedServices || [];
    const taxations: Record<string, RoomStayTaxation> = formData?.taxations || {};

    const stayDates =
      formData?.checkIn && formData?.checkOut
        ? new ExtraSelectionEntity(formData.checkIn, formData.checkOut, []).getStayDates()
        : [];
    const buildDateQuantity = (
      quantity: number,
      dateSelectionType?: keyof typeof DATE_SELECTION_TYPE
    ) => {
      if (stayDates.length === 0) {
        return {};
      }
      if (dateSelectionType === DATE_SELECTION_TYPE.NONE || !dateSelectionType) {
        return { [stayDates[0]]: quantity };
      }
      return Object.fromEntries(stayDates.map(date => [date, 1]));
    };
    const selectedTickers = new Set(selectedServices.map(service => service.ticker));
    const taxationExtras: SelectedService[] = Object.values(taxations)
      .filter(taxation => taxation.fromExtra)
      .map(taxation => {
        const catalogExtra = findCatalogExtraForTaxation(taxation.ticker, transformedExtrasList);
        const ticker = catalogExtra?.ticker ?? taxation.ticker.replace(TAXATION_TICKER_PREFIX, "");
        const quantity = taxation.quantity ?? 1;
        const hasDateQuantity =
          taxation.dateQuantity && Object.keys(taxation.dateQuantity).length > 0;
        return {
          ticker,
          name: taxation.name,
          quantity,
          dateQuantity: hasDateQuantity
            ? taxation.dateQuantity
            : buildDateQuantity(quantity, catalogExtra?.dateSelectionType),
          amount: {
            amount: taxation.amount,
            currency: info?.defaultCurrency || "EUR",
          },
          type: catalogExtra?.type as ExtraType,
          dateSelectionType: catalogExtra?.dateSelectionType,
          percentage: catalogExtra?.percentage,
        };
      })
      .filter(
        taxationExtra =>
          !selectedTickers.has(taxationExtra.ticker) &&
          !deletedTaxationTickers.includes(taxationExtra.ticker)
      );

    return [...selectedServices, ...taxationExtras];
  }, [
    formData?.selectedServices,
    formData?.taxations,
    formData?.checkIn,
    formData?.checkOut,
    transformedExtrasList,
    info?.defaultCurrency,
    deletedTaxationTickers,
  ]);
  const insurance: InsuranceSelected | undefined = useMemo(() => formData?.insurance, [formData]);

  const selectedServicesWithFlags = useMemo(() => {
    return extras.map((service: SelectedService) => {
      const matchingExtra = transformedExtrasList.find(extra => extra.ticker === service.ticker);
      if (matchingExtra) {
        return {
          ...service,
          type: matchingExtra.type,
          dateSelectionType: matchingExtra.dateSelectionType,
          percentage: matchingExtra.percentage,
          appliesToGuests: matchingExtra.appliesToGuests,
          dateQuantity: service.dateQuantity ?? matchingExtra.dateQuantity,
          isAppliedToCurrentInventoryTicker: matchingExtra.isAppliedToCurrentInventoryTicker,
          isAvailable: matchingExtra.isAvailable,
          isRestricted: matchingExtra.isRestricted,
        };
      }
      return {
        ...service,
        isAppliedToCurrentInventoryTicker: true,
        isAvailable: true,
        isRestricted: false,
      };
    });
  }, [extras, transformedExtrasList]);

  // The grid rebuilds the daily values asynchronously, so they only describe the current form
  // once this matches; until then the in-between state is not worth reporting.
  const gridInSync =
    formData?.gridSyncedFor ===
    buildGridSyncKey({
      checkIn: formData?.checkIn,
      checkOut: formData?.checkOut,
      inventoryTicker: formData?.inventoryTicker,
    });

  const invalidDailyValueDates = useMemo(
    () =>
      gridInSync
        ? getInvalidDailyValueDates({
            checkIn: formData?.checkIn,
            checkOut: formData?.checkOut,
            dailyValues: formData?.dailyValues,
            originalDailyValues: formData?.originalDailyValues,
            userPricedDates: formData?.userPricedDates,
            canEditPrices: !isRestrictedModification,
          })
        : new Set<string>(),
    [
      gridInSync,
      formData?.checkIn,
      formData?.checkOut,
      formData?.dailyValues,
      formData?.originalDailyValues,
      formData?.userPricedDates,
      isRestrictedModification,
    ]
  );
  const datesMismatch = invalidDailyValueDates.size > 0;

  // Not gated on gridInSync: these dates travel with the daily values they were derived from,
  // and a night the property is not selling must never be waved through by a stale stamp.
  const unratedStayDates = useMemo(
    () =>
      getUnratedStayDates({
        checkIn: formData?.checkIn,
        checkOut: formData?.checkOut,
        unratedDates: formData?.unratedDates,
      }),
    [formData?.checkIn, formData?.checkOut, formData?.unratedDates]
  );

  const highlightedDates = useMemo(
    () => new Set([...invalidDailyValueDates, ...unratedStayDates]),
    [invalidDailyValueDates, unratedStayDates]
  );

  const gridErrors = useMemo(() => {
    const errors: string[] = [];
    if (datesMismatch) {
      errors.push(intl.formatMessage(messages.dailyValuesDatesMismatch));
    }
    if (unratedStayDates.size > 0) {
      errors.push(intl.formatMessage(messages.noRateLoadedForDates));
    }
    return errors;
  }, [datesMismatch, unratedStayDates, intl]);

  const quantity = useMemo(() => formData?.quantity || 1, [formData]);
  const comments = useMemo(() => formData?.comments || "", [formData]);
  const internalNotes = useMemo(() => formData?.internalNotes || "", [formData]);
  const guaranteeAmount = useMemo(() => formData?.guaranteeAmount || 0, [formData]);

  const handleUserKeyPress = useCallback(event => {
    const { keyCode } = event;
    if (keyCode === 13) {
      event.preventDefault();
    }
  }, []);

  // Necessary so form is not submitted on enter key press
  useEffect(() => {
    window.addEventListener("keydown", handleUserKeyPress);
    return () => {
      window.removeEventListener("keydown", handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  const calculateTotalValues = useCallback(
    ({
      currentFinalPrices,
      currentExtras,
      currentQuantity,
      currentInsurance,
    }: {
      currentFinalPrices?: FinalPrice[];
      currentExtras?: SelectedService[];
      currentQuantity?: string | number;
      currentInsurance?: InsuranceSelected;
    }) => {
      const prices = currentFinalPrices || finalPrices;
      const quant = Number(currentQuantity || quantity);
      const extra = currentExtras || extras;
      const valuesTotal = (
        prices.reduce(
          (accum: number, curr: { finalPrice: number | undefined }) =>
            Number(curr.finalPrice || 0) + accum,
          0
        ) * quant
      ).toFixed(2);

      const extrasTotal = extra
        .reduce(
          (accum: number, curr: { amount: Amount; quantity: number }) =>
            Number(curr.amount.amount) + accum,
          0
        )
        .toFixed(2);

      let grandTotal = Number(valuesTotal) + Number(extrasTotal);
      if (currentInsurance) {
        grandTotal += Number(currentInsurance.amount);
      }

      return { valuesTotal, extrasTotal, grandTotal: grandTotal.toFixed(2) };
    },
    [finalPrices, extras, quantity]
  );

  useEffect(() => {
    if (formData?.selectedServices && selectedServicesWithFlags.length > 0) {
      const hasUnmergedExtras =
        transformedExtrasList.length > 0 &&
        selectedServicesWithFlags.length !== formData.selectedServices.length;
      const needsUpdate =
        hasUnmergedExtras ||
        formData.selectedServices.some((service: SelectedService, index: number) => {
          const updatedService = selectedServicesWithFlags[index];
          return updatedService && service.quantity !== updatedService.quantity;
        });

      if (needsUpdate) {
        const updatedExtras = selectedServicesWithFlags
          .map(extra => ({
            ...extra,
            isAppliedToCurrentInventoryTicker: undefined,
            isAvailable: undefined,
            isRestricted: undefined,
          }))
          .map(({ isAppliedToCurrentInventoryTicker, isAvailable, isRestricted, ...rest }) => rest);

        const { grandTotal } = calculateTotalValues({
          currentExtras: updatedExtras as any,
          currentInsurance: formData.insurance,
        });

        onChange({
          ...formData,
          selectedServices: updatedExtras,
          totalAmount: grandTotal,
        });
      }
    }
  }, [
    selectedServicesWithFlags,
    currentInventory,
    formData,
    onChange,
    calculateTotalValues,
    transformedExtrasList,
  ]);

  const valuesTotal = useMemo(() => {
    const { valuesTotal } = calculateTotalValues({});
    return valuesTotal;
  }, [calculateTotalValues]);
  const extrasTotal = useMemo(() => {
    const { extrasTotal } = calculateTotalValues({});
    return extrasTotal;
  }, [calculateTotalValues]);
  const grandTotal = useMemo(() => {
    const { grandTotal } = calculateTotalValues({ currentInsurance: insurance });
    return grandTotal;
  }, [calculateTotalValues, insurance]);

  const fetchAri = useCallback<(startDate: string, endDate: string) => Promise<AriValues>>(
    async (startDate: string, endDate: string): Promise<AriValues> => {
      return await apiClient.get<any>(
        `/api/hotel/${propertyTicker}/ari-values?startDate=${startDate}&endDate=${endDate}&onlyInventories=true&inventoryTicker=${selectedInventoryTicker}`
      );
    },
    [propertyTicker, apiClient, selectedInventoryTicker]
  );

  const calculateFinalPrices = useCallback(
    (rows: Row[], currentFormData: any, newCheckIn?, newCheckOut?, newSelectedInventoryTicker?) => {
      if (rows) {
        let rowsClone = cloneDeep(rows);
        rowsClone = rowsClone.map(row => {
          row.finalPrice = row.appliedPrice;

          if (row.discount && row.appliedPrice) {
            row.finalPrice = Number((row.appliedPrice - Number(row.discount)).toFixed(2));
          }

          if (typeof row.discount === "string" && row.discount.includes("%") && row.appliedPrice) {
            const discount = row.discount.substring(0, row.discount.length - 1);
            row.finalPrice = Number(
              (row.appliedPrice - (row.appliedPrice / 100) * Number(discount)).toFixed(2)
            );
          }

          return row;
        });
        const finPrices = rowsClone.map(it => ({ date: it.date, finalPrice: it.finalPrice }));
        setFinalPrices(finPrices);

        const dailyValues = finPrices
          .filter(it => it.finalPrice || it.finalPrice === 0)
          .map(it => ({ ticker: it.date, amount: it.finalPrice }));

        // Derived from the very same rows as the daily values so the two can never disagree:
        // a stay night with no sellable rate is one a restricted user has no way to price.
        const stayNights = new Set(
          getStayNightTickers(
            newCheckIn ?? currentFormData.checkIn,
            newCheckOut ?? currentFormData.checkOut
          )
        );
        const unratedDates = isRestrictedModification
          ? rowsClone
              .filter(
                row =>
                  stayNights.has(row.date) &&
                  !(typeof row.ariPrice === "number" && row.ariPrice > 0)
              )
              .map(row => row.date)
          : [];
        const { grandTotal } = calculateTotalValues({
          currentFinalPrices: finPrices,
          currentExtras: currentFormData.selectedServices || [],
          currentInsurance: currentFormData.insurance,
        });

        const updates: any = {
          dailyValues,
          totalAmount: grandTotal,
          unratedDates,
        };

        if (newSelectedInventoryTicker !== undefined) {
          updates.inventoryTicker = newSelectedInventoryTicker;
        }
        if (newCheckIn !== undefined) {
          updates.checkIn = newCheckIn;
        }
        if (newCheckOut !== undefined) {
          updates.checkOut = newCheckOut;
        }

        // Stamp what these prices were computed for. A date change updates the pickers straight
        // away while the rates are still loading, and the daily values only catch up here, so
        // without this the checks would report the in-between state as an error for a second.
        updates.gridSyncedFor = buildGridSyncKey({
          checkIn: newCheckIn ?? currentFormData.checkIn,
          checkOut: newCheckOut ?? currentFormData.checkOut,
          inventoryTicker: newSelectedInventoryTicker ?? currentFormData.inventoryTicker,
        });

        onChange({
          ...currentFormData,
          ...updates,
        });
      }
    },
    [onChange, calculateTotalValues, isRestrictedModification]
  );

  const fetchData = useCallback(
    async function fetchData(
      currentFormData: any,
      params?: {
        newCheckIn?: string;
        newCheckOut?: string;
        newSelectedInventoryTicker?: string;
      }
    ) {
      const checkIn = params?.newCheckIn || currentFormData.checkIn;
      const checkOut = params?.newCheckOut || currentFormData.checkOut;
      const inventoryTicker = params?.newSelectedInventoryTicker || selectedInventoryTicker;
      const parsedCheckIn = startOfDay(new Date(checkIn));
      const parsedCheckOut = startOfDay(new Date(checkOut));
      if (
        ((currentFormData.dailyValues &&
          (prevCheckIn.current !== checkIn || prevCheckOut.current !== checkOut)) ||
          prevSelectedInventoryTicker.current !== inventoryTicker) &&
        isBefore(parsedCheckIn, parsedCheckOut)
      ) {
        const seq = ++fetchSeq.current;
        const threeDayBefore = subDays(parsedCheckIn, 3);
        const threeDayAfter = addDays(parsedCheckOut, 3);
        const startDate = format(threeDayBefore, "yyyy-MM-dd");
        const endDate = format(threeDayAfter, "yyyy-MM-dd");

        // Degrade gracefully instead of aborting: generateRows falls back to "-" for the
        // loaded rate when there are no ARI values, and the grid is then filled from the
        // reservation's own stored daily values below. Aborting here would leave the user
        // with an empty, unusable price grid. Logged rather than surfaced, so the grid stays
        // editable without interrupting the user.
        let ariValues: AriValues | undefined;
        let promos: any[] = [];
        try {
          ariValues = await fetchAri(startDate, endDate);
          promos = await apiClient.get<any>(
            `/api/property/${propertyTicker}/engine/administration/promo/`
          );
        } catch (error) {
          logSafeError("Could not load the ARI values or the promos for the price grid", error);
        }

        // A newer fetchData call (or an unmount) happened while the requests were in flight:
        // this response belongs to a stale date range / inventory and must not overwrite the grid.
        if (seq !== fetchSeq.current) {
          return;
        }

        const sortedPromos = promos
          .filter((promo: any) => promo.isActive)
          .map((promo: any) => {
            let openBookingStartDate = "";
            let openBookingEndDate = "";
            let multiOpenBooking = false;
            if (
              Array.isArray(promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value) &&
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value.length > 1
            ) {
              multiOpenBooking = true;
            }
            if (
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value &&
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value[0]?.startDate
            ) {
              const index = promo.restrictions.OPEN_BOOKING.restrictionValue.value[0].startDate.indexOf(
                "T"
              );
              openBookingStartDate = promo.restrictions.OPEN_BOOKING.restrictionValue.value[0].startDate.substring(
                0,
                index
              );
            }

            if (
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value &&
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value[0]?.endDate
            ) {
              const index = promo.restrictions.OPEN_BOOKING.restrictionValue.value[0].endDate.indexOf(
                "T"
              );
              openBookingEndDate = promo.restrictions.OPEN_BOOKING.restrictionValue.value[0].endDate.substring(
                0,
                index
              );
            }

            return {
              ticker: promo.ticker,
              amount: promo?.rate?.rateValue?.value,
              type: promo.rate.rateType,
              openTravelStartDate: `${
                (promo?.restrictions?.OPEN_TRAVEL?.restrictionValue?.value &&
                  promo?.restrictions?.OPEN_TRAVEL?.restrictionValue?.value[0]?.startDate) ||
                ""
              }`,
              openTravelEndDate: `${
                (promo?.restrictions?.OPEN_TRAVEL?.restrictionValue?.value &&
                  promo?.restrictions?.OPEN_TRAVEL?.restrictionValue?.value[0]?.endDate) ||
                ""
              }`,
              openBookingStartDate,
              openBookingEndDate,
              multiOpenBooking,
              minStay: promo?.restrictions?.MIN_STAY?.restrictionValue?.value?.duration,
              maxStay: promo?.restrictions?.MAX_STAY?.restrictionValue?.value?.duration,
              maxNotice: promo?.restrictions?.MIN_NOTICE?.restrictionValue?.value?.duration,
              minNotice: promo?.restrictions?.MAX_NOTICE?.restrictionValue?.value?.duration,
              discountedUnits: promo?.discountPeriodicity?.discountedUnits,
              applyEveryNumberOfUnits: promo?.discountPeriodicity?.applyEveryNumberOfUnits,
              group: promo?.group,
            };
          });
        const items: Row[] = generateRows({
          ariValues,
          selectedInventoryTicker: inventoryTicker,
          sortedPromos,
          checkIn,
          checkOut,
          initial: true,
        });
        const existingDailyDates = new Set(
          currentFormData.dailyValues.map((it: { ticker: string }) => it.ticker)
        );
        currentFormData.dailyValues.forEach((it: { amount: number; ticker: string }) => {
          const itemIndex = items.findIndex(item => item.date === it.ticker);
          if (
            itemIndex !== -1 &&
            isWithinInterval(startOfDay(new Date(it.ticker)), {
              start: parsedCheckIn,
              end: subDays(parsedCheckOut, 1),
            })
          ) {
            items[itemIndex].appliedPrice = it.amount;
            if (currentFormData.originalPromoCodes) {
              items[itemIndex].promos = currentFormData.originalPromoCodes.map((it: any) => ({
                ticker: it.name,
                amount: it.percentage
                  ? Math.abs(it.value)
                  : {
                      amount: Math.abs(it.value),
                      currency: it.totalDiscount?.currency,
                    },
                type: it.percentage ? RATE_TYPE.PERCENTAGE : RATE_TYPE.CONSTANT,
              }));
            }
          }
        });

        // For restricted modification users, auto-fill new days with ARI price
        // since they cannot manually set prices. Days with no sellable rate are left alone here
        // and reported by calculateFinalPrices, which flags them from these same rows.
        if (isRestrictedModification) {
          items.forEach(item => {
            const itemDate = startOfDay(new Date(item.date));
            if (
              !existingDailyDates.has(item.date) &&
              typeof item.ariPrice === "number" &&
              item.ariPrice > 0 &&
              isWithinInterval(itemDate, {
                start: parsedCheckIn,
                end: subDays(parsedCheckOut, 1),
              })
            ) {
              item.appliedPrice = item.ariPrice;
            }
          });
        }

        prevCheckIn.current = checkIn;
        prevCheckOut.current = checkOut;
        prevSelectedInventoryTicker.current = inventoryTicker;
        setRows(items);
        calculateFinalPrices(
          items,
          currentFormData,
          params?.newCheckIn,
          params?.newCheckOut,
          params?.newSelectedInventoryTicker
        );
      }
    },
    [
      calculateFinalPrices,
      propertyTicker,
      selectedInventoryTicker,
      apiClient,
      fetchAri,
      isRestrictedModification,
    ]
  );

  useEffect(() => {
    const seqRef = fetchSeq;
    return () => {
      // Invalidate any in-flight fetchData so it cannot update state after unmount.
      seqRef.current++;
    };
  }, []);

  useEffect(() => {
    if (!initialAriFetched) {
      fetchData(formData);
      setInitialAriFetched(true);
    }
  }, [initialAriFetched, fetchData, formData]);

  useEffect(() => {
    // Set on cleanup (rows/dates/inventory changed or unmount) so a stale response is dropped.
    let cancelled = false;
    async function fetchGhostDate() {
      let ariValues;
      const realDateEnd = rows && rows.length - 3;
      let currentGhostDate;
      if (rows.length && rows[2].appliedPrice) {
        const dayBeforeStart = subDays(new Date(rows[0].date), 1);
        const starDate = format(dayBeforeStart, "yyyy-MM-dd");
        const twoDaysBeforeStart = subDays(new Date(rows[0].date), newAriEnabled ? 0 : 1);
        const endDate = format(twoDaysBeforeStart, "yyyy-MM-dd");
        ariValues = await fetchAri(starDate, endDate);
        currentGhostDate = dayBeforeStart;
      }
      if (rows.length && realDateEnd && rows[realDateEnd].appliedPrice) {
        const index = rows.length;
        const dayAfterEnd = addDays(new Date(rows[index - 1].date), 1);
        const starDate = format(dayAfterEnd, "yyyy-MM-dd");
        const twoDaysAfterEnd = addDays(new Date(rows[index - 1].date), newAriEnabled ? 2 : 1);
        const endDate = format(twoDaysAfterEnd, "yyyy-MM-dd");
        ariValues = await fetchAri(starDate, endDate);
        currentGhostDate = dayAfterEnd;
      }
      if (cancelled) {
        return;
      }
      if (ariValues) {
        const promos = await apiClient.get<any>(
          `/api/property/${propertyTicker}/engine/administration/promo/`
        );
        if (cancelled) {
          return;
        }
        const sortedPromos = promos.map((promo: any) => ({
          ticker: promo.ticker,
          amount: promo?.rate?.rateValue?.value,
          type: promo.rate.rateType,
          group: promo?.group,
        }));
        const items: Row[] = generateRows({
          ariValues,
          selectedInventoryTicker,
          sortedPromos,
          checkIn: formData.checkIn,
          checkOut: formData.checkOut,
          currentGhostDate,
        });

        if (items.length) {
          const ro = [...rows, ...items].sort((a, b) => {
            if (isBefore(new Date(a.date), new Date(b.date))) {
              return -1;
            }
            if (isAfter(new Date(a.date), new Date(b.date))) {
              return 1;
            }
            return 0;
          });
          setRows(ro);
          calculateFinalPrices(ro, formData);
        }
      }
    }
    // These rows only pad the grid with the days adjacent to the stay, so a failure here is
    // not worth interrupting the user for: the grid itself is already rendered by now. Logged
    // rather than left as an unhandled rejection.
    fetchGhostDate().catch(error => {
      logSafeError("Could not load the adjacent-day ARI values for the price grid", error);
    });
    return () => {
      cancelled = true;
    };
  }, [
    calculateFinalPrices,
    rows,
    apiClient,
    propertyTicker,
    selectedInventoryTicker,
    fetchAri,
    newAriEnabled,
    formData,
  ]);

  const handleSelectorChange = useCallback(
    option => {
      const formDta = cloneDeep(formData);

      if (formDta.selectedServices && formDta.selectedServices.length > 0) {
        setReferenceExtras(prev => [...prev, ...formDta.selectedServices]);
        formDta.selectedServices = [];
        const { grandTotal } = calculateTotalValues({
          currentExtras: [],
          currentInsurance: formDta.insurance,
        });
        formDta.totalAmount = grandTotal;
      }
      formDta.inventoryTicker = option.value;
      formDta.userPricedDates = [];

      setSelectedInventoryTicker(option.value);

      onChange(formDta);
      fetchData(formDta, { newSelectedInventoryTicker: option.value });
    },
    [onChange, formData, fetchData, calculateTotalValues]
  );

  const handleChange = useCallback(
    (value, date, type, regex) => {
      const rowsClone = cloneDeep(rows);
      const rowIndex = rowsClone?.findIndex(it => it.date === date);
      value = regex ? (String(value).match(regex) ? value : 0) : value;
      //@ts-ignore
      rowsClone[rowIndex][type] = value;
      setRows(rowsClone);
      // Typing into a row settles that night even when the result is 0, so it stops being
      // reported as unpriced.
      calculateFinalPrices(rowsClone, {
        ...formData,
        userPricedDates: [...new Set([...(formData.userPricedDates || []), date])],
      });
    },
    [rows, calculateFinalPrices, formData]
  );

  const handleDeletePrice = useCallback(
    (index, type) => {
      const rowsClone = cloneDeep(rows);
      delete rowsClone[index].appliedPrice;
      delete rowsClone[index].discount;
      if (type === "top") {
        rowsClone.shift();
      } else if (type === "bottom") {
        rowsClone.pop();
      }
      setRows(rowsClone);
      calculateFinalPrices(rowsClone, formData);
    },
    [rows, calculateFinalPrices, formData]
  );

  const handleDeleteExtras = useCallback(
    (ticker: string) => {
      swal({
        closeOnClickOutside: false,
        closeOnEsc: false,
        title: intl.formatMessage(messages.confirmation),
        icon: "warning",
        buttons: {
          cancel: {
            text: intl.formatMessage(messages.cancel),
            value: false,
            visible: true,
          },
          confirm: {
            text: intl.formatMessage(messages.ok),
            value: true,
            closeModal: true,
          },
        },
        dangerMode: true,
      }).then(async willDelete => {
        if (willDelete) {
          const newExtras = extras
            .filter((it: SelectedService) => it.ticker !== ticker)
            .map((it: SelectedService) => ({ ...it }));
          const isTaxationExtra = Object.values(
            (formData.taxations || {}) as Record<string, RoomStayTaxation>
          ).some(
            taxation =>
              taxation.fromExtra &&
              resolveTaxationExtraTicker(taxation.ticker, transformedExtrasList) === ticker
          );
          if (isTaxationExtra) {
            setDeletedTaxationTickers(prev => (prev.includes(ticker) ? prev : [...prev, ticker]));
          }
          const { grandTotal } = calculateTotalValues({
            currentExtras: newExtras,
            currentInsurance: formData.insurance,
          });
          onChange({ ...formData, selectedServices: newExtras, totalAmount: grandTotal });
        }
        return false;
      });
    },
    [extras, formData, onChange, calculateTotalValues, intl, transformedExtrasList]
  );

  const handleOpenCancellationPoliciesModal = useCallback(
    (open: boolean) => setIsCancellationPoliciesModalOpen(open),
    []
  );

  const handleOpenExtraSelectionModal = useCallback((open: boolean, extraTicker?: string) => {
    setIsExtraSelectionModalOpen(open);
    if (open && extraTicker) {
      setScrollToExtraTicker(extraTicker);
    } else if (!open) {
      setScrollToExtraTicker(null);
    }
  }, []);

  const handleOpenPriceModificationModal = useCallback((extra: SelectedService) => {
    setSelectedServiceForPriceEdit(extra);
    setIsPriceModificationModalOpen(true);
  }, []);

  const handleClosePriceModificationModal = useCallback(() => {
    setIsPriceModificationModalOpen(false);
    setSelectedServiceForPriceEdit(null);
  }, []);

  const handlePriceModificationSubmit = useCallback(
    (updatedService: SelectedService) => {
      const updatedExtras = formData.selectedServices.map((extra: SelectedService) =>
        extra.ticker === updatedService.ticker ? updatedService : extra
      );
      const { grandTotal } = calculateTotalValues({
        currentExtras: updatedExtras,
        currentInsurance: formData.insurance,
      });

      onChange({
        ...formData,
        selectedServices: updatedExtras,
        totalAmount: grandTotal,
      });

      handleClosePriceModificationModal();
    },
    [formData, onChange, calculateTotalValues, handleClosePriceModificationModal]
  );

  const handleExtraSelectionConfirm = useCallback(
    (selectedExtras: Extra[]) => {
      const { grandTotal } = calculateTotalValues({
        currentExtras: selectedExtras as any,
        currentInsurance: formData.insurance,
      });

      onChange({
        ...formData,
        selectedServices: selectedExtras as any,
        totalAmount: grandTotal,
      });

      handleOpenExtraSelectionModal(false);
    },
    [formData, calculateTotalValues, onChange, handleOpenExtraSelectionModal]
  );
  const handleChangeDeposit = useCallback(
    data => {
      if (regex.test(data.event.target.value)) {
        onChange({
          ...formData,
          guaranteeAmount: data.event.target.value,
        });
      }
    },
    [formData, onChange]
  );

  const handleChangeQuantity = useCallback(
    data => {
      const { grandTotal } = calculateTotalValues({
        currentQuantity: data.event.target.value,
        currentInsurance: formData.insurance,
      });
      onChange({ ...formData, quantity: Number(data.event.target.value), totalAmount: grandTotal });
    },
    [formData, onChange, calculateTotalValues]
  );

  const handleChangeString = useCallback(
    (type, data) => {
      onChange({ ...formData, [type]: data.event.target.value });
    },
    [formData, onChange]
  );

  const handleChangeDate = useCallback(
    (data, type) => {
      if (formData.selectedServices && formData.selectedServices.length > 0) {
        setReferenceExtras(prev => {
          const newExtras = formData.selectedServices.filter(
            (service: SelectedService) => !prev.some(ref => ref.ticker === service.ticker)
          );
          return [...prev, ...newExtras];
        });
      }

      const { grandTotal } = calculateTotalValues({
        currentExtras: [],
        currentInsurance: formData.insurance,
      });

      const updatedFormData = {
        ...formData,
        [type]: data.value,
        selectedServices: [],
        totalAmount: grandTotal,
        userPricedDates: [],
      };

      if (type === "checkIn") {
        fetchData(updatedFormData, { newCheckIn: data.value });
      } else {
        fetchData(updatedFormData, { newCheckOut: data.value });
      }
      onChange(updatedFormData);
    },
    [formData, onChange, fetchData, calculateTotalValues]
  );

  const options = useMemo(() => {
    const optns: { value: string; label: string }[] = [];
    //@ts-ignore
    if (Array.isArray(schema?.properties?.inventoryTicker?.enum)) {
      //@ts-ignore
      schema.properties.inventoryTicker.enum.forEach((it, idx) =>
        optns.push({
          value: it as string,
          //@ts-ignore
          label: schema?.properties?.inventoryTicker?.enumNames[idx],
        })
      );
    }
    return optns;
  }, [schema]);

  const selectedOption = useMemo(() => {
    return options.find(it => it.value === formData?.inventoryTicker);
  }, [formData, options]);

  const minDate = useMemo(() => {
    const date = addDays(new Date(formData.checkIn), 1);
    return format(date, "yyyy-MM-dd");
  }, [formData.checkIn]);

  const cellBaseStyles =
    "min-h-[25px] flex align-items-center justify-content-center h-full bg-gray-100 border-r border-gray-300 px-2";
  const headerCellStyles =
    "flex align-items-center justify-content-center border-r border-gray-300 px-2 py-3";
  const actionCellStyles = "px-2 min-h-[25px] flex justify-center justify-content-center gap-2";
  const iconWrapperStyles = "flex align-items-center";

  return (
    <StyledModificationPriceManagement>
      <>
        <label>
          <FormattedMessage {...messages.room} /> ({formData.inventoryTicker})
        </label>
        <AccommodationSelector
          hasVirtualInventories={hasVirtualInventories}
          locale={locale.currentLocale.substring(0, 2)}
          inventoryTicker={formData.inventoryTicker}
          propertyTicker={propertyTicker}
          onChange={handleSelectorChange}
          inventories={propertyData?.inventories}
          disabled={selectorDisabled}
          options={options}
          selectedOption={selectedOption}
        />
        <label>
          <FormattedMessage {...messages.quantity} />
        </label>
        <InputGroup
          type="number"
          value={quantity}
          field={{ fieldName: "quantity" }}
          onChange={data => handleChangeQuantity(data)}
          disabled={false}
          readOnly
        />
      </>
      <>
        <label>
          <FormattedMessage {...messages.checkIn} />
        </label>
        <DatePickerGroupWrapper>
          <DatePickerGroup
            formValues={{ startDate: formData.checkIn, endDate: formData.checkOut }}
            hasErrors={!!rawErrors || datesMismatch}
            onChange={data => {
              handleChangeDate(data, "checkIn");
            }}
            disabled={datesDisabled}
            value={formData.checkIn}
            field={{
              fieldName: "startDate",
              selectsStart: true,
              isRange: true,
              startField: "startDate",
              endField: "endDate",
            }}
            isClearable={!datesDisabled}
          />
          <label>
            {" "}
            <FormattedMessage {...messages.checkOut} />
          </label>
          <DatePickerGroup
            formValues={{ startDate: formData.checkIn, endDate: formData.checkOut }}
            hasErrors={!!rawErrors || datesMismatch}
            onChange={data => {
              handleChangeDate(data, "checkOut");
            }}
            disabled={datesDisabled}
            value={formData.checkOut}
            field={{
              fieldName: "endDate",
              selectsEnd: true,
              isRange: true,
              startField: "startDate",
              endField: "endDate",
            }}
            minDate={minDate}
            isClearable={!datesDisabled}
          />
        </DatePickerGroupWrapper>
        {gridErrors.length > 0 && <FieldErrorList errors={gridErrors} />}
      </>
      <BookingDetails>
        <label>
          <FormattedMessage {...messages.comments} />
        </label>
        <TextAreaGroup
          value={comments}
          field={{ fieldName: "comments" }}
          onChange={data => handleChangeString("comments", data)}
          rows={10}
          readOnly={notAllowCustomerCommentEdit}
        />

        <label>
          <FormattedMessage {...messages.internalNotes} />
        </label>
        <TextAreaGroup
          value={internalNotes}
          field={{ fieldName: "internalNotes" }}
          onChange={data => handleChangeString("internalNotes", data)}
          rows={10}
          readOnly={false}
        />
      </BookingDetails>
      {showPriceGrid && (
        <>
          <h5>
            <FormattedMessage {...messages.netPrice} />
          </h5>
          <HelpTextMessageNoMargin>
            * <FormattedMessage {...messages.usePoint} />
          </HelpTextMessageNoMargin>
          <HelpTextMessage>
            * <FormattedMessage {...messages.noRate} />
          </HelpTextMessage>
          {gridErrors.length > 0 && <FieldErrorList errors={gridErrors} />}
          <Grid>
            <GridDate header>
              <FormattedMessage {...messages.dateHeader} />
            </GridDate>
            <GridAriPrice header>
              <FormattedMessage {...messages.ariPriceHeader} />
            </GridAriPrice>
            <GridAppliedPrice header>
              <FormattedMessage {...messages.appliedPriceHeader} />
              <StyledFontAwesomeIconWrapper
                className={"styled-font-awesome-icon-wrapper"}
                onClick={() => handleOpenCancellationPoliciesModal(true)}>
                <FontAwesomeIcon icon={faInfo} size={"xs"} />
              </StyledFontAwesomeIconWrapper>
            </GridAppliedPrice>
            <GridDiscount header>
              <FormattedMessage {...messages.discountHeader} />
              <StyledFontAwesomeIconWrapper
                data-tip="React-tooltip"
                data-for="discount"
                className={"styled-font-awesome-icon-wrapper"}>
                <FontAwesomeIcon icon={faInfo} size={"xs"} />
              </StyledFontAwesomeIconWrapper>
              <StyledReactTooltip id="discount" effect="solid" multiline={true}>
                <FormattedMessage {...messages.discountTooltip} />
              </StyledReactTooltip>
            </GridDiscount>
            <GridFinalPrice header>
              <FormattedMessage {...messages.finalPriceHeader} />
            </GridFinalPrice>
            <GridSeparator />
            <GridRestrictions header>
              <FormattedMessage {...messages.restrictionsHeader} />
            </GridRestrictions>
            <GridPromos header>
              <FormattedMessage {...messages.promosHeader} />
            </GridPromos>
            {rows &&
              rows.map((row, index) => (
                <GridRow
                  row={row}
                  index={index}
                  rows={rows}
                  formData={formData}
                  handleChange={handleChange}
                  intl={intl}
                  finalPrices={finalPrices}
                  handleDeletePrice={handleDeletePrice}
                  selectedOption={selectedOption}
                  defaultCurrency={info.defaultCurrency}
                  regex={regex}
                  readOnlyPrices={!!isRestrictedModification}
                  invalidDates={highlightedDates}
                />
              ))}
          </Grid>
          <NetPriceTotalWrapper>
            <h6>
              <FormattedMessage {...messages.netPriceTotal} />: <span>{valuesTotal}</span>
            </h6>
            <div>
              <span>
                * <FormattedMessage {...messages.netPriceTotalHelpText} />
              </span>
            </div>
          </NetPriceTotalWrapper>
        </>
      )}

      <>
        <h5>
          <FormattedMessage {...messages.extras} />
        </h5>
        {(!!extras?.length || !!referenceExtras?.length) && (
          <div className="text-[#2E3862] text-[13px]">
            {referenceExtras?.length > 0 && (
              <div className="font-semibold text-red-600">
                <FormattedMessage {...messages.extrasModifiedWarning} />
              </div>
            )}
            <div className="pt-[20px]">
              <div className="grid grid-cols-7 font-semibold border border-gray-300">
                <div className={headerCellStyles}>
                  <FormattedMessage {...messages.extraTicker} />
                </div>
                <div className={headerCellStyles}>
                  <FormattedMessage {...messages.extraName} />
                </div>
                <div className={headerCellStyles}>
                  <FormattedMessage {...messages.extraType} />
                </div>
                <div className={`${headerCellStyles} text-center`}>
                  <FormattedMessage {...messages.unit} />
                </div>
                <div className={`${headerCellStyles} text-center`}>
                  <FormattedMessage {...messages.price} />
                </div>
                <div className={`${headerCellStyles} text-center`}>
                  <FormattedMessage {...messages.total} />
                </div>
                <div className="flex align-items-center justify-content-center px-2 py-3">
                  <FormattedMessage {...messages.actions} />
                </div>
              </div>
              {referenceExtras.map((extra: SelectedService, index: number) => (
                <div
                  key={`reference-${index}`}
                  className="grid grid-cols-7 border-x border-b border-gray-300 items-center">
                  <div className={cellBaseStyles}>{extra.ticker}</div>
                  <div className={cellBaseStyles}>{extra.name}</div>
                  <div className={cellBaseStyles}>{extra.type}</div>
                  <div className={cellBaseStyles}>{extra.quantity}</div>
                  <div className={`${cellBaseStyles} gap-2`}>
                    {extra.quantity > 0
                      ? (extra.amount.amount / extra.quantity).toFixed(2)
                      : "0.00"}
                    <div className={iconWrapperStyles}>
                      <div className={`${iconWrapperStyles} opacity-30 cursor-not-allowed`}>
                        <FontAwesomeIcon icon={faPencilAlt} size={"2xs"} />
                      </div>
                    </div>
                  </div>
                  <div className={cellBaseStyles}>{Number(extra.amount.amount).toFixed(2)}</div>
                  <div className={actionCellStyles}>
                    <div className={`${iconWrapperStyles} opacity-30 cursor-not-allowed`}>
                      <FontAwesomeIcon icon={faPencilAlt} size={"2xs"} />
                    </div>
                    <div className={`${iconWrapperStyles} opacity-30 cursor-not-allowed`}>
                      <FontAwesomeIcon icon={faTrash} size={"2xs"} />
                    </div>
                  </div>
                </div>
              ))}
              {extras.map((extra: SelectedService, index: number) => (
                <div
                  key={`active-${index}`}
                  className="grid grid-cols-7 border-x border-b border-gray-300 items-center">
                  <div className={cellBaseStyles}>{extra.ticker}</div>
                  <div className={cellBaseStyles}>{extra.name}</div>
                  <div className={cellBaseStyles}>{extra.type}</div>
                  <div className={cellBaseStyles}>{extra.quantity}</div>
                  <div className={`${cellBaseStyles} gap-2`}>
                    {extra.quantity > 0
                      ? (extra.amount.amount / extra.quantity).toFixed(2)
                      : "0.00"}
                    <div className={iconWrapperStyles}>
                      <StyledFontAwesomeIconWrapper
                        className={"styled-font-awesome-icon-wrapper"}
                        onClick={() => handleOpenPriceModificationModal(extra)}>
                        <FontAwesomeIcon icon={faPencilAlt} size={"2xs"} />
                      </StyledFontAwesomeIconWrapper>
                    </div>
                  </div>
                  <div className={cellBaseStyles}>{Number(extra.amount.amount).toFixed(2)}</div>
                  <div className={actionCellStyles}>
                    <div className={iconWrapperStyles}>
                      <StyledFontAwesomeIconWrapper
                        className={"styled-font-awesome-icon-wrapper"}
                        onClick={() => handleOpenExtraSelectionModal(true, extra.ticker)}>
                        <FontAwesomeIcon icon={faPencilAlt} size={"2xs"} />
                      </StyledFontAwesomeIconWrapper>
                    </div>
                    <div className={iconWrapperStyles}>
                      <StyledFontAwesomeIconWrapper
                        className={"styled-font-awesome-icon-wrapper"}
                        onClick={() => handleDeleteExtras(extra.ticker)}>
                        <FontAwesomeIcon icon={faTrash} size={"xs"} />
                      </StyledFontAwesomeIconWrapper>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          className="btn btn-primary my-[20px] cursor-pointer"
          onClick={() => handleOpenExtraSelectionModal(true)}>
          <FormattedMessage {...messages.addExtras} />
        </div>
        <ExtrasTotal>
          <h6>
            <FormattedMessage {...messages.extrasTotal} />: <span>{extrasTotal}</span>
          </h6>
          {insurance && (
            <h6>
              <FormattedMessage {...messages.insurance} />: <span>{insurance.amount}</span>
            </h6>
          )}
        </ExtrasTotal>
      </>

      <GrandTotalWrapper>
        <FormattedMessage {...messages.grandTotal} />: {grandTotal}
      </GrandTotalWrapper>
      <PriceLine>
        <span>
          {" "}
          <FormattedMessage {...messages.deposit} />:
        </span>
        <InputGroup
          type="text"
          value={guaranteeAmount}
          field={{ fieldName: "guaranteeAmount" }}
          onChange={data => handleChangeDeposit(data)}
          disabled={false}
          readOnly={false}
        />
      </PriceLine>
      <CustomModal
        isOpen={isCancellationPoliciesModalOpen}
        onClose={() => handleOpenCancellationPoliciesModal(false)}
        title={intl.formatMessage(messages.cancellationPolicies)}>
        <CancellationPolicies
          formData={{
            ...formData,
            checkIn: formData.checkIn,
            checkOut: formData.checkOut,
            totalAmount: Number(grandTotal),
            dailyValues: formData.dailyValues,
          }}
        />
      </CustomModal>
      <CustomModal
        isOpen={isExtraSelectionModalOpen}
        onClose={() => handleOpenExtraSelectionModal(false)}
        title={intl.formatMessage(messages.extras)}>
        <ExtraSelection
          catalogExtras={transformedExtrasList}
          selectedExtras={selectedServicesWithFlags as ExtraWithFlags[]}
          checkIn={formData.checkIn}
          checkOut={formData.checkOut}
          onCancel={() => handleOpenExtraSelectionModal(false)}
          onConfirm={handleExtraSelectionConfirm}
          originalExtras={originalExtras}
          originalName={originalName}
          currentInventoryTicker={formData.inventoryTicker}
          scrollToExtraTicker={scrollToExtraTicker}
          currentInventory={currentInventory}
        />
      </CustomModal>
      <CustomModal
        isOpen={Boolean(isPriceModificationModalOpen && selectedServiceForPriceEdit)}
        onClose={handleClosePriceModificationModal}
        title={intl.formatMessage(messages.editExtraPrice)}>
        {selectedServiceForPriceEdit && (
          <ExtraPriceModification
            extra={selectedServiceForPriceEdit}
            onSubmit={handlePriceModificationSubmit}
            onClose={handleClosePriceModificationModal}
          />
        )}
      </CustomModal>
    </StyledModificationPriceManagement>
  );
}
